import React, { useRef, useState, useEffect, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { Eraser } from "lucide-react";

/**
 * Robust signature pad using Pointer Events + ResizeObserver.
 *
 * Why this rewrite (prior version was unresponsive on iOS Safari):
 * - React synthetic touch events are attached as PASSIVE listeners on iOS,
 *   so `e.preventDefault()` in onTouchMove was silently ignored — the page
 *   scrolled and the canvas never received the move.
 * - Canvas internal dimensions were measured once at mount. If the element
 *   was 0x0 at that instant (animation, lazy parent render, dialog), all
 *   subsequent strokes targeted a 0x0 bitmap and disappeared.
 *
 * The fix:
 * - Use native pointer events via addEventListener with { passive: false }.
 *   Pointer events normalise mouse/touch/stylus across browsers.
 * - ResizeObserver re-syncs the canvas backing buffer with the rendered size.
 * - setPointerCapture so the drag never gets "lost" when the finger leaves
 *   the canvas bounds mid-stroke.
 * - touch-action: none is applied directly on the element (in addition to
 *   the CSS class) to guarantee no browser pan/zoom hijack on iOS.
 */
export default function SignaturePad({ value, onChange, testId = "signature-pad" }) {
  const canvasRef = useRef(null);
  const containerRef = useRef(null);
  const drawingRef = useRef(false);
  const lastPointRef = useRef(null);
  const strokesRef = useRef([]); // array of strokes; each stroke is array of {x,y}
  const [hasStrokes, setHasStrokes] = useState(!!value);

  // Re-render the canvas content from strokesRef (used after resize)
  const redraw = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    const rect = canvas.getBoundingClientRect();
    ctx.fillStyle = "#ffffff";
    ctx.fillRect(0, 0, rect.width, rect.height);
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    ctx.strokeStyle = "#0f172a";
    ctx.lineWidth = 2.5;
    for (const stroke of strokesRef.current) {
      if (stroke.length < 1) continue;
      ctx.beginPath();
      ctx.moveTo(stroke[0].x, stroke[0].y);
      for (let i = 1; i < stroke.length; i++) {
        ctx.lineTo(stroke[i].x, stroke[i].y);
      }
      ctx.stroke();
    }
  }, []);

  // Size the canvas backing buffer to match its rendered size × devicePixelRatio
  const resizeCanvas = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const rect = canvas.getBoundingClientRect();
    if (rect.width === 0 || rect.height === 0) return;
    const dpr = window.devicePixelRatio || 1;
    canvas.width = Math.round(rect.width * dpr);
    canvas.height = Math.round(rect.height * dpr);
    const ctx = canvas.getContext("2d");
    ctx.setTransform(1, 0, 0, 1, 0, 0); // reset before scaling
    ctx.scale(dpr, dpr);
    redraw();
  }, [redraw]);

  // Convert a PointerEvent's clientX/clientY into canvas-local CSS coordinates
  const posFromEvent = useCallback((e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    return { x: e.clientX - rect.left, y: e.clientY - rect.top };
  }, []);

  // Pointer down — start a new stroke
  const handlePointerDown = useCallback((e) => {
    e.preventDefault();
    const canvas = canvasRef.current;
    if (!canvas) return;
    try { canvas.setPointerCapture(e.pointerId); } catch { /* ignore */ }
    const p = posFromEvent(e);
    strokesRef.current.push([p]);
    lastPointRef.current = p;
    drawingRef.current = true;
    const ctx = canvas.getContext("2d");
    ctx.beginPath();
    ctx.moveTo(p.x, p.y);
    // Draw a dot so a tap (no movement) still leaves a mark
    ctx.lineTo(p.x + 0.01, p.y + 0.01);
    ctx.stroke();
  }, [posFromEvent]);

  // Pointer move — extend the active stroke
  const handlePointerMove = useCallback((e) => {
    if (!drawingRef.current) return;
    e.preventDefault();
    const canvas = canvasRef.current;
    if (!canvas) return;
    const p = posFromEvent(e);
    const stroke = strokesRef.current[strokesRef.current.length - 1];
    stroke.push(p);
    const ctx = canvas.getContext("2d");
    ctx.lineTo(p.x, p.y);
    ctx.stroke();
    lastPointRef.current = p;
  }, [posFromEvent]);

  // Pointer up / cancel — commit the stroke and notify parent
  const handlePointerEnd = useCallback((e) => {
    if (!drawingRef.current) return;
    drawingRef.current = false;
    const canvas = canvasRef.current;
    if (!canvas) return;
    try { canvas.releasePointerCapture(e.pointerId); } catch { /* ignore */ }
    setHasStrokes(true);
    try {
      const dataUrl = canvas.toDataURL("image/png");
      onChange?.(dataUrl);
    } catch {
      /* ignore export errors */
    }
  }, [onChange]);

  const clear = useCallback(() => {
    strokesRef.current = [];
    setHasStrokes(false);
    redraw();
    onChange?.("");
  }, [onChange, redraw]);

  // Wire up native, non-passive listeners + a ResizeObserver
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    // Initial sizing
    resizeCanvas();

    // Pointer event handlers (native, non-passive to allow preventDefault)
    const down = (e) => handlePointerDown(e);
    const move = (e) => handlePointerMove(e);
    const up = (e) => handlePointerEnd(e);
    const cancel = (e) => handlePointerEnd(e);

    const opts = { passive: false };
    canvas.addEventListener("pointerdown", down, opts);
    canvas.addEventListener("pointermove", move, opts);
    canvas.addEventListener("pointerup", up, opts);
    canvas.addEventListener("pointercancel", cancel, opts);
    canvas.addEventListener("pointerleave", up, opts);

    // ResizeObserver re-syncs the canvas backing buffer when the visual size
    // changes (rotation, dialog open, parent layout change, etc.)
    let ro;
    if (typeof ResizeObserver !== "undefined") {
      ro = new ResizeObserver(() => resizeCanvas());
      ro.observe(canvas);
    }
    // Belt + suspenders: handle window resize / orientationchange explicitly
    const onWindowResize = () => resizeCanvas();
    window.addEventListener("resize", onWindowResize);
    window.addEventListener("orientationchange", onWindowResize);

    return () => {
      canvas.removeEventListener("pointerdown", down, opts);
      canvas.removeEventListener("pointermove", move, opts);
      canvas.removeEventListener("pointerup", up, opts);
      canvas.removeEventListener("pointercancel", cancel, opts);
      canvas.removeEventListener("pointerleave", up, opts);
      if (ro) ro.disconnect();
      window.removeEventListener("resize", onWindowResize);
      window.removeEventListener("orientationchange", onWindowResize);
    };
  }, [handlePointerDown, handlePointerMove, handlePointerEnd, resizeCanvas]);

  return (
    <div className="space-y-2" ref={containerRef}>
      <canvas
        ref={canvasRef}
        className="signature-canvas"
        data-testid={testId}
        style={{ touchAction: "none", WebkitUserSelect: "none", userSelect: "none" }}
      />
      <div className="flex items-center justify-between">
        <p className="text-xs text-slate-500">{hasStrokes ? "Signed ✓" : "Sign with your finger or mouse"}</p>
        <Button type="button" variant="ghost" size="sm" onClick={clear} data-testid="signature-clear-btn">
          <Eraser className="h-4 w-4 mr-1" /> Clear
        </Button>
      </div>
    </div>
  );
}
